import { type ColumnFiltersState } from '@apps/web/lib/table';
import {
    type ShiftStage,
    type ShiftStatus,
} from '@libraries/typescript/api_client';

/**
 * Unified status values that combine ShiftStatus and ShiftStage for display.
 */
export type UnifiedShiftStatus =
    | 'open'
    | 'not_scheduled'
    | 'assigned'
    | 'pending_confirmation'
    | 'confirmed'
    | 'declined'
    | 'no_response'
    | 'needs_review'
    | 'in_progress'
    | 'completed'
    | 'cancelled'
    | 'needs_attention'
    | 'clock_in_reminder'
    | 'clock_out_reminder'
    | 'confirmation_follow_up'
    | 'missing'
    | 'unknown';

/**
 * Returns true when the EHR, rather than Phoebe outreach, is the source of a
 * pending-confirmation state.
 */
export function isEhrPendingConfirmation(
    shiftStatus: ShiftStatus | null | undefined,
    shiftStage: ShiftStage | null | undefined,
): boolean {
    return (
        shiftStatus === 'pending_confirmation' &&
        shiftStage !== 'pending_confirmation' &&
        shiftStage !== 'confirmation_follow_up'
    );
}

export const ALL_UNIFIED_STATUS_VALUES: Array<UnifiedShiftStatus> = [
    'open',
    'not_scheduled',
    'assigned',
    'pending_confirmation',
    'confirmed',
    'declined',
    'no_response',
    'needs_review',
    'in_progress',
    'completed',
    'cancelled',
    'needs_attention',
    'clock_in_reminder',
    'clock_out_reminder',
    'confirmation_follow_up',
    'missing',
];

const ROUTE_SHIFT_STATUSES = ['open', 'needs_attention'] as const;

/**
 * Statuses the Shifts page accepts as a `status` deep-link search param.
 */
export type RouteShiftStatus = (typeof ROUTE_SHIFT_STATUSES)[number];

/**
 * Type guard for the `status` deep-link param. Use this wherever a search
 * value is narrowed to RouteShiftStatus so route navigations round-trip
 * every supported status instead of silently dropping newer ones.
 */
export function isRouteShiftStatus(value: unknown): value is RouteShiftStatus {
    return (ROUTE_SHIFT_STATUSES as ReadonlyArray<unknown>).includes(value);
}

export const COVERAGE_UNIFIED_STATUSES: ReadonlyArray<UnifiedShiftStatus> = [
    'open',
];

/**
 * Status options offered by the Shifts status filter.
 */
export function statusFilterOptions({
    clockInOutEnabled,
}: {
    readonly clockInOutEnabled: boolean;
}): Array<UnifiedShiftStatus> {
    if (clockInOutEnabled) {
        return [...ALL_UNIFIED_STATUS_VALUES];
    }
    return ALL_UNIFIED_STATUS_VALUES.filter(
        (status) =>
            status !== 'clock_in_reminder' && status !== 'clock_out_reminder',
    );
}

function includedRouteStatuses(
    filters: ColumnFiltersState,
): Array<UnifiedShiftStatus> {
    const value = filters.find((filter) => filter.id === 'unified_status')
        ?.value as { include?: Array<UnifiedShiftStatus> } | undefined;
    return value?.include ?? [];
}

/**
 * Synchronizes the single status represented in a Shifts deep link while
 * preserving richer local filters that cannot be encoded in that URL.
 */
export function syncRouteStatusFilter(
    filters: ColumnFiltersState,
    previousStatus: RouteShiftStatus | undefined,
    nextStatus: RouteShiftStatus | undefined,
): ColumnFiltersState {
    const included = includedRouteStatuses(filters);
    const filtersWithoutStatus = filters.filter(
        (filter) => filter.id !== 'unified_status',
    );

    if (nextStatus) {
        if (included.length === 1 && included[0] === nextStatus) {
            return filters;
        }
        return [
            ...filtersWithoutStatus,
            {
                id: 'unified_status',
                value: { include: [nextStatus] },
            },
        ];
    }

    if (
        previousStatus &&
        included.length === 1 &&
        included[0] === previousStatus
    ) {
        return filtersWithoutStatus;
    }

    return filters;
}

/**
 * Resolves the combined status from ShiftStatus and ShiftStage.
 */
export function resolveShiftStatus(
    shiftStatus: ShiftStatus | null | undefined,
    shiftStage: ShiftStage | null | undefined,
): UnifiedShiftStatus {
    if (shiftStatus === 'missing') {
        return 'missing';
    }
    if (shiftStage === 'cancelled' || shiftStatus === 'cancelled') {
        return 'cancelled';
    }

    if (
        shiftStatus === 'completed' ||
        shiftStage === 'clocked_out' ||
        shiftStage === 'care_log_audited'
    ) {
        return 'completed';
    }

    if (shiftStatus === 'in_progress' || shiftStage === 'clocked_in') {
        return 'in_progress';
    }

    if (
        shiftStage === 'clock_out_reminder' ||
        shiftStage === 'clock_in_reminder'
    ) {
        return shiftStage;
    }

    if (shiftStatus === 'assigned_and_confirmed') {
        return 'confirmed';
    }

    if (
        shiftStage === 'confirmed' ||
        shiftStage === 'declined' ||
        shiftStage === 'no_response' ||
        shiftStage === 'needs_review' ||
        shiftStage === 'confirmation_follow_up' ||
        shiftStage === 'pending_confirmation'
    ) {
        return shiftStage;
    }

    // WellSky exposes Pending Confirmation as a shift status. Use that EHR
    // state when Phoebe has not recorded a more specific confirmation result.
    if (shiftStatus === 'pending_confirmation') {
        return 'pending_confirmation';
    }

    if (shiftStatus === 'needs_attention') {
        return 'needs_attention';
    }
    if (shiftStage === 'assigned' || shiftStatus === 'assigned') {
        return 'assigned';
    }
    if (shiftStage === 'pending_assignment' || shiftStatus === 'open') {
        return 'open';
    }
    if (shiftStatus === 'not_scheduled') {
        return shiftStatus;
    }
    return 'unknown';
}
